import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useNavigate, useSearchParams } from "react-router-dom";

import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { ApiLojaBaseURL, ApiLojaGet, ApiLojaPost } from "data/ApiLoja";
import { userPermissions } from "hooks/Permissions";

import { ReleaseDashboard } from ".";

type ReleaseStatus = "success" | "failed" | "processing" | string;

type ReleaseProcessListItem = {
  order_id: string;
  cpf?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  status: ReleaseStatus;
  current_step?: string | null;
  last_error_step?: string | null;
  last_error_message?: string | null;
  attempt_count?: number | null;
  updated_at?: string | null;
  finished_at?: string | null;
};

type ReleaseStepSummaryItem = {
  status: ReleaseStatus;
  message?: string | null;
  attempt?: number | null;
  updated_at?: string | null;
  details?: unknown;
};

type ReleaseProcessEvent = {
  id: string;
  step: string;
  status: ReleaseStatus;
  message?: string | null;
  attempt?: number | null;
  created_at?: string | null;
  details?: unknown;
};

type ReleaseProcessDetails = ReleaseProcessListItem & {
  step_summary?: Record<string, ReleaseStepSummaryItem>;
  events?: ReleaseProcessEvent[];
};

type ReleaseProcessResponse = {
  items: ReleaseProcessListItem[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

type HealthServiceKey = "vtex" | "ftec" | "ad" | "emoodle" | "ouromoderno" | "google" | "sheetdb";

type HealthCheckResponse = {
  status: "ok" | "degraded" | "error";
  services: Record<HealthServiceKey, boolean>;
  failedServices: HealthServiceKey[];
  checkedAt: string;
  message?: string;
};

type Filters = {
  orderId: string;
  cpf: string;
  currentStep: string;
  lastErrorStep: string;
  status: string;
};

const INITIAL_FILTERS: Filters = {
  orderId: "",
  cpf: "",
  currentStep: "",
  lastErrorStep: "",
  status: "",
};

const statusMeta: Record<
  string,
  {
    label: string;
    color: "success" | "error" | "warning" | "default";
    icon: JSX.Element;
  }
> = {
  success: {
    label: "Sucesso",
    color: "success",
    icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
  },
  failed: {
    label: "Falha",
    color: "error",
    icon: <ErrorOutlineRoundedIcon fontSize="small" />,
  },
  processing: {
    label: "Processando",
    color: "warning",
    icon: <AutorenewRoundedIcon fontSize="small" />,
  },
};

const healthLabels: Record<HealthServiceKey, string> = {
  vtex: "VTEX",
  ftec: "FTEC",
  ad: "AD",
  emoodle: "E-moodle",
  ouromoderno: "Ouro Moderno",
  google: "Google",
  sheetdb: "SheetDB",
};

const primaryButtonSx = { color: "#ffffff", fontWeight: 700 };
const secondaryButtonSx = {
  color: "#1f2937",
  borderColor: alpha("#1f2937", 0.22),
  fontWeight: 700,
};
const textButtonSx = { color: "#1f2937", fontWeight: 700 };
const monitoredOrdersGridColumns =
  "minmax(240px, 2.6fr) minmax(140px, 1.15fr) minmax(150px, 1.2fr) minmax(90px, 0.7fr) minmax(160px, 1.25fr) minmax(150px, 1.1fr)";
const healthServiceErrorStepMap: Record<HealthServiceKey, string[]> = {
  vtex: ["resolve_notification_email", "vtex_pick", "load_order_items", "invoice_order"],
  ftec: ["ensure_academic_user"],
  ad: ["validate_ad"],
  emoodle: ["validate_emoodle"],
  ouromoderno: ["validate_ouro_moderno"],
  google: [],
  sheetdb: [],
};

const getStatusMeta = (status?: string | null) =>
  statusMeta[status || ""] || {
    label: status || "-",
    color: "default" as const,
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  };

const formatDate = (value?: string | null) => {
  if (!value) return "-";

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatStepLabel = (value?: string | null) => {
  if (!value) return "-";

  return value
    .split("_")
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
    .join(" ");
};

const formatJson = (value?: unknown) => {
  if (!value) return "-";

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as { message?: string } | undefined)?.message ||
      error.message ||
      fallback
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

function List() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { update: canRetry } = userPermissions({
    updatePermision: ReleaseDashboard.Permissions.retry,
  });

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [items, setItems] = useState<ReleaseProcessListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [refreshingPanel, setRefreshingPanel] = useState(false);
  const [retryingId, setRetryingId] = useState<string>();
  const [resendingAccessId, setResendingAccessId] = useState<string>();
  const [error, setError] = useState<string>();
  const [healthError, setHealthError] = useState<string>();
  const [detailsError, setDetailsError] = useState<string>();
  const [healthCheck, setHealthCheck] = useState<HealthCheckResponse>();
  const [selectedOrderId, setSelectedOrderId] = useState<string>();
  const [selectedProcess, setSelectedProcess] = useState<ReleaseProcessDetails>();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success" as "success" | "error",
    title: "",
    content: "",
  });

  const loadHealthCheck = async () => {
    setLoadingHealth(true);
    setHealthError(undefined);

    try {
      const response = await ApiLojaGet<HealthCheckResponse>("/health-check");
      setHealthCheck(response.data);

      const pinnedOrderId = searchParams.get("orderId")?.trim();
      const hasIncident =
        response.data?.status !== "ok" && Array.isArray(response.data?.failedServices);

      if (!pinnedOrderId && hasIncident && response.data.failedServices.length > 0) {
        await applyIncidentFilters(response.data.failedServices);
      }
    } catch (currentError) {
      setHealthError(getErrorMessage(currentError, "Erro ao consultar health check."));
      setHealthCheck(undefined);
    } finally {
      setLoadingHealth(false);
    }
  };

  const loadProcesses = async (currentFilters = filters, limit = 50) => {
    setLoading(true);
    setError(undefined);

    try {
      const response = await ApiLojaGet<ReleaseProcessResponse>("/release-processes", {
        params: {
          ...(currentFilters.orderId ? { orderId: currentFilters.orderId.trim() } : {}),
          ...(currentFilters.cpf ? { cpf: currentFilters.cpf.trim() } : {}),
          ...(currentFilters.currentStep ? { currentStep: currentFilters.currentStep.trim() } : {}),
          ...(currentFilters.lastErrorStep
            ? { lastErrorStep: currentFilters.lastErrorStep.trim() }
            : {}),
          ...(currentFilters.status ? { status: currentFilters.status } : {}),
          limit,
        },
      });

      const nextItems = response.data?.items || [];
      setItems(nextItems);
      return nextItems;
    } catch (currentError) {
      setError(getErrorMessage(currentError, "Erro ao consultar processos de liberação."));
      setItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const applyIncidentFilters = async (failedServices: HealthServiceKey[]) => {
    const failedSteps = Array.from(
      new Set(failedServices.flatMap((serviceKey) => healthServiceErrorStepMap[serviceKey] || []))
    );

    const nextFilters: Filters = {
      ...INITIAL_FILTERS,
      status: "failed",
      lastErrorStep: failedSteps.join(","),
    };

    setFilters(nextFilters);
    await loadProcesses(nextFilters, 5000);
  };

  const loadDetails = async (orderId: string, openModal = true) => {
    setSelectedOrderId(orderId);
    setLoadingDetails(true);
    setDetailsError(undefined);

    if (openModal) {
      setIsDetailsOpen(true);
    }

    try {
      const response = await ApiLojaGet<ReleaseProcessDetails>(
        `/release-processes/${encodeURIComponent(orderId)}`
      );

      setSelectedProcess(response.data);
    } catch (currentError) {
      setSelectedProcess(undefined);
      setDetailsError(getErrorMessage(currentError, "Erro ao consultar detalhes do pedido."));
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setSelectedProcess(undefined);
    setSelectedOrderId(undefined);
    setDetailsError(undefined);
  };

  const handleRetry = async (orderId: string) => {
    const confirmed = window.confirm(`Executar retry do pedido ${orderId}?`);

    if (!confirmed) return;

    setRetryingId(orderId);

    try {
      const response = await ApiLojaPost<{ message?: string }>(
        `/release-processes/${encodeURIComponent(orderId)}/retry`
      );

      setSnackbar({
        open: true,
        color: "success",
        title: "Retry executado",
        content: response.data?.message || `O pedido ${orderId} foi reenfileirado.`,
      });

      await Promise.all([loadProcesses(filters), loadHealthCheck()]);

      if (selectedOrderId === orderId || isDetailsOpen) {
        await loadDetails(orderId, isDetailsOpen);
      }
    } catch (currentError) {
      setSnackbar({
        open: true,
        color: "error",
        title: "Erro ao executar retry",
        content: getErrorMessage(currentError, "Não foi possível reenviar o pedido."),
      });
    } finally {
      setRetryingId(undefined);
    }
  };

  const handleResendAccess = async (orderId: string) => {
    const confirmed = window.confirm(
      `Redefinir a senha do aluno e reenviar o e-mail de acesso do pedido ${orderId}?`
    );

    if (!confirmed) return;

    setResendingAccessId(orderId);

    try {
      const response = await ApiLojaPost<{ message?: string }>(
        `/release-processes/${encodeURIComponent(orderId)}/resend-access`
      );

      setSnackbar({
        open: true,
        color: "success",
        title: "Acesso reenviado",
        content: response.data?.message || `O acesso do pedido ${orderId} foi reenviado.`,
      });

      await loadProcesses(filters);

      if (selectedOrderId === orderId || isDetailsOpen) {
        await loadDetails(orderId, isDetailsOpen);
      }
    } catch (currentError) {
      setSnackbar({
        open: true,
        color: "error",
        title: "Erro ao reenviar acesso",
        content: getErrorMessage(
          currentError,
          "Não foi possível redefinir a senha e reenviar o acesso."
        ),
      });
    } finally {
      setResendingAccessId(undefined);
    }
  };

  const handleRefreshPanel = async () => {
    if (refreshingPanel) return;

    setRefreshingPanel(true);

    try {
      const tasks: Promise<unknown>[] = [loadProcesses(filters), loadHealthCheck()];

      if (selectedOrderId && isDetailsOpen) {
        tasks.push(loadDetails(selectedOrderId, true));
      }

      await Promise.all(tasks);
    } finally {
      setRefreshingPanel(false);
    }
  };

  const openOrderPage = (orderId: string) => {
    navigate(`/ecommerce/pedidos/${encodeURIComponent(orderId)}`);
  };

  const applyFilters = async () => {
    const nextParams: Record<string, string> = {};

    if (filters.orderId.trim()) {
      nextParams.orderId = filters.orderId.trim();
    }

    const currentOrderId = searchParams.get("orderId")?.trim() || "";

    if (currentOrderId !== (nextParams.orderId || "")) {
      setSearchParams(nextParams);
      return;
    }

    await loadProcesses(filters);
  };

  const clearFilters = async () => {
    setFilters(INITIAL_FILTERS);
    closeDetails();

    if (searchParams.get("orderId")) {
      setSearchParams({});
      return;
    }

    await loadProcesses(INITIAL_FILTERS);
  };

  useEffect(() => {
    const orderId = searchParams.get("orderId")?.trim() || "";
    const nextFilters = {
      ...INITIAL_FILTERS,
      orderId,
    };

    setFilters(nextFilters);
    loadProcesses(nextFilters);
    loadHealthCheck();

    if (orderId) {
      loadDetails(orderId, true);
    } else {
      closeDetails();
    }
  }, [searchParams]);

  const successCount = items.filter((item) => item.status === "success").length;
  const failedCount = items.filter((item) => item.status === "failed").length;
  const processingCount = items.filter((item) => item.status === "processing").length;
  const healthOkCount = Object.values(healthCheck?.services || {}).filter(Boolean).length;

  const totalHealthServices = Object.keys(healthCheck?.services || {}).length;

  const summaryCards = useMemo(
    () => [
      {
        title: "Total monitorado",
        value: items.length,
        caption: "Pedidos na busca atual",
        icon: <SearchRoundedIcon fontSize="small" />,
        accent: "#25476a",
      },
      {
        title: "Com sucesso",
        value: successCount,
        caption: "Fluxos concluídos",
        icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
        accent: "#2d6a4f",
      },
      {
        title: "Com falha",
        value: failedCount,
        caption: "Precisam de atencao",
        icon: <ErrorOutlineRoundedIcon fontSize="small" />,
        accent: "#b42318",
      },
      {
        title: "Em andamento",
        value: processingCount,
        caption: "Aguardando conclusão",
        icon: <AutorenewRoundedIcon fontSize="small" />,
        accent: "#b76e00",
      },
      {
        title: "Serviços saudáveis",
        value: healthCheck ? `${healthOkCount}/${totalHealthServices || 0}` : "-",
        caption:
          healthCheck?.status === "degraded" ? "Health check degradado" : "Monitoramento ativo",
        icon: <HealthAndSafetyRoundedIcon fontSize="small" />,
        accent: healthCheck?.status === "degraded" ? "#b45309" : "#0f766e",
      },
    ],
    [
      failedCount,
      healthCheck,
      healthOkCount,
      items.length,
      processingCount,
      successCount,
      totalHealthServices,
    ]
  );

  const stepSummaryEntries = useMemo(
    () =>
      Object.entries(selectedProcess?.step_summary || {}).sort((a, b) => {
        const dateA = a[1]?.updated_at ? new Date(a[1].updated_at).getTime() : 0;
        const dateB = b[1]?.updated_at ? new Date(b[1].updated_at).getTime() : 0;

        return dateB - dateA;
      }),
    [selectedProcess?.step_summary]
  );

  const recentEvents = useMemo(
    () => (selectedProcess?.events || []).slice(0, 20),
    [selectedProcess?.events]
  );

  const healthEntries = useMemo(
    () =>
      (Object.entries(healthCheck?.services || {}) as [HealthServiceKey, boolean][]).map(
        ([serviceKey, isUp]) => ({
          key: serviceKey,
          label: healthLabels[serviceKey],
          isUp,
        })
      ),
    [healthCheck?.services]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Stack spacing={3} mt={3} mb={4}>
        <Card
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            background: "linear-gradient(135deg, #f7fbff 0%, #edf4fb 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", lg: "center" }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h4" fontWeight={700} color="#1f2937">
                  Libera Acesso
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.75}>
                  Acompanhe a saúde dos serviços, identifique falhas e abra o pedido correto em um
                  clique.
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" mt={1.25}>
                  API monitorada: {ApiLojaBaseURL || "REACT_APP_API_LOJA_URL não configurada"}
                </Typography>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button
                  variant="outlined"
                  startIcon={
                    refreshingPanel ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshRoundedIcon />
                    )
                  }
                  sx={secondaryButtonSx}
                  disabled={refreshingPanel}
                  onClick={handleRefreshPanel}
                >
                  {refreshingPanel ? "Atualizando..." : "Atualizar painel"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<LaunchRoundedIcon />}
                  sx={primaryButtonSx}
                  onClick={() => navigate("/ecommerce/pedidos")}
                >
                  Ir para pedidos
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
              xl: "repeat(5, minmax(0, 1fr))",
            },
            alignItems: "stretch",
          }}
        >
          {summaryCards.map((card) => (
            <Box key={card.title}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 3,
                  border: `1px solid ${alpha(card.accent, 0.15)}`,
                  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
                }}
              >
                <CardContent sx={{ height: "100%" }}>
                  <Stack spacing={2} justifyContent="space-between" height="100%">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography variant="button" color="text.secondary">
                        {card.title}
                      </Typography>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2,
                          color: card.accent,
                          backgroundColor: alpha(card.accent, 0.12),
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>

                    <Box>
                      <Typography variant="h4" fontWeight={700} color="#111827">
                        {card.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {card.caption}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Health Check
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status em tempo real dos serviços envolvidos na liberação.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<HealthAndSafetyRoundedIcon />}
                sx={secondaryButtonSx}
                onClick={loadHealthCheck}
                disabled={loadingHealth}
              >
                Atualizar health
              </Button>
            </Stack>

            {healthError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {healthError}
              </Alert>
            )}

            {!healthError && healthCheck && (
              <Alert severity={healthCheck.status === "ok" ? "success" : "warning"} sx={{ mb: 2 }}>
                {healthCheck.status === "ok"
                  ? "Todos os serviços monitorados responderam normalmente."
                  : `Serviço(s) com instabilidade: ${healthCheck.failedServices
                      .map((service) => healthLabels[service])
                      .join(", ")}`}
              </Alert>
            )}

            <Grid container spacing={2}>
              {loadingHealth && !healthCheck ? (
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="center" py={3}>
                    <CircularProgress color="info" />
                  </Stack>
                </Grid>
              ) : (
                healthEntries.map((service) => (
                  <Grid item xs={12} sm={6} lg={4} xl={2} key={service.key}>
                    <Box
                      sx={{
                        p: 2,
                        height: "100%",
                        minHeight: 108,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(service.isUp ? "#2d6a4f" : "#b42318", 0.2)}`,
                        backgroundColor: alpha(service.isUp ? "#2d6a4f" : "#b42318", 0.05),
                      }}
                    >
                      <Stack spacing={1.5} justifyContent="space-between" sx={{ height: "100%" }}>
                        <Typography
                          variant="button"
                          color="#111827"
                          sx={{
                            minHeight: 44,
                            lineHeight: 1.35,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {service.label}
                        </Typography>
                        <Chip
                          size="small"
                          label={service.isUp ? "Online" : "Offline"}
                          color={service.isUp ? "success" : "error"}
                          variant={service.isUp ? "filled" : "outlined"}
                          sx={{ alignSelf: "flex-start" }}
                        />
                      </Stack>
                    </Box>
                  </Grid>
                ))
              )}
            </Grid>

            <Typography variant="caption" display="block" color="text.secondary" mt={2}>
              Última checagem: {formatDate(healthCheck?.checkedAt)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Filtros
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Busque um pedido específico ou refine o monitoramento por CPF, etapa e status.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Pedido"
                    value={filters.orderId}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, orderId: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="CPF"
                    value={filters.cpf}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, cpf: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Etapa atual"
                    value={filters.currentStep}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, currentStep: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Etapa com erro"
                    value={filters.lastErrorStep}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, lastErrorStep: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={filters.status}
                    onChange={(event) =>
                      setFilters((current) => ({ ...current, status: event.target.value }))
                    }
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="success">Sucesso</MenuItem>
                    <MenuItem value="failed">Falha</MenuItem>
                    <MenuItem value="processing">Processando</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <Button variant="contained" sx={primaryButtonSx} onClick={applyFilters}>
                  Aplicar filtros
                </Button>
                <Button variant="outlined" sx={secondaryButtonSx} onClick={clearFilters}>
                  Limpar filtros
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              px={3}
              py={2.5}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Pedidos monitorados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clique em um pedido para abrir o histórico completo.
                </Typography>
              </Box>

              <Chip
                label={`${items.length} registro(s)`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            <Divider />

            {error && (
              <Box px={3} pt={2}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <Box
              sx={{
                maxHeight: "calc(100vh - 340px)",
                overflowX: "auto",
                overflowY: "auto",
                pt: error ? 2 : 0,
              }}
            >
              <Box sx={{ minWidth: 980 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: monitoredOrdersGridColumns,
                    gap: 2,
                    alignItems: "center",
                    px: 2.5,
                    py: 2,
                    borderRadius: 2.5,
                    backgroundColor: "#f8fafc",
                    border: `1px solid ${alpha("#1f2937", 0.08)}`,
                  }}
                >
                  {["Pedido", "Status", "Etapa atual", "Tentativas", "Atualizado em", "Ações"].map(
                    (label, index) => (
                      <Typography
                        key={label}
                        variant="button"
                        color="#111827"
                        sx={{
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          textAlign: index >= 3 ? "center" : "left",
                        }}
                      >
                        {label}
                      </Typography>
                    )
                  )}
                </Box>

                {loading ? (
                  <Box py={6} display="flex" justifyContent="center">
                    <CircularProgress color="info" />
                  </Box>
                ) : items.length === 0 ? (
                  <Box py={5} textAlign="center">
                    Nenhum processo encontrado com os filtros atuais.
                  </Box>
                ) : (
                  <Stack spacing={0}>
                    {items.map((item) => {
                      const itemStatus = getStatusMeta(item.status);

                      return (
                        <Box
                          key={item.order_id}
                          onClick={() => loadDetails(item.order_id, true)}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: monitoredOrdersGridColumns,
                            gap: 2,
                            alignItems: "center",
                            px: 2.5,
                            py: 2.25,
                            cursor: "pointer",
                            borderBottom: `1px solid ${alpha("#1f2937", 0.08)}`,
                            transition: "background-color 120ms ease",
                            "&:hover": {
                              backgroundColor: alpha("#1f2937", 0.02),
                            },
                          }}
                        >
                          <Box sx={{ overflow: "hidden" }}>
                            <Typography variant="button" display="block" color="#111827">
                              {item.order_id}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.45,
                              }}
                              title={item.customer_name || "Cliente não identificado"}
                            >
                              {item.customer_name || "Cliente não identificado"}
                            </Typography>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{
                                mt: 0.25,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                lineHeight: 1.45,
                              }}
                              title={item.customer_email || item.cpf || "-"}
                            >
                              {item.customer_email || item.cpf || "-"}
                            </Typography>
                          </Box>

                          <Box>
                            <Chip
                              size="small"
                              icon={itemStatus.icon}
                              label={itemStatus.label}
                              color={itemStatus.color}
                              variant={itemStatus.color === "default" ? "outlined" : "filled"}
                              sx={{ maxWidth: "100%" }}
                            />
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={formatStepLabel(item.current_step)}
                          >
                            {formatStepLabel(item.current_step)}
                          </Typography>

                          <Typography variant="body2" textAlign="center">
                            {item.attempt_count || 0}
                          </Typography>

                          <Typography variant="body2" textAlign="center" whiteSpace="nowrap">
                            {formatDate(item.updated_at || item.finished_at)}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="nowrap"
                            justifyContent="center"
                            alignItems="center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              title="Ver detalhes"
                              sx={{
                                color: "#1f2937",
                                border: `1px solid ${alpha("#1f2937", 0.14)}`,
                                borderRadius: 2,
                                backgroundColor: alpha("#1f2937", 0.03),
                              }}
                              onClick={() => loadDetails(item.order_id, true)}
                            >
                              <VisibilityRoundedIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Abrir pedido"
                              sx={{
                                color: "#1f2937",
                                border: `1px solid ${alpha("#1f2937", 0.14)}`,
                                borderRadius: 2,
                                backgroundColor: alpha("#1f2937", 0.03),
                              }}
                              onClick={() => openOrderPage(item.order_id)}
                            >
                              <LaunchRoundedIcon fontSize="small" />
                            </IconButton>
                            {canRetry && (
                              <IconButton
                                size="small"
                                title="Redefinir senha e reenviar acesso"
                                sx={{
                                  color:
                                    item.status !== "success" ? alpha("#1f2937", 0.35) : "#1f2937",
                                  border: `1px solid ${alpha("#1f2937", 0.14)}`,
                                  borderRadius: 2,
                                  backgroundColor: alpha("#1f2937", 0.03),
                                }}
                                disabled={
                                  item.status !== "success" || resendingAccessId === item.order_id
                                }
                                onClick={() => handleResendAccess(item.order_id)}
                              >
                                {resendingAccessId === item.order_id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <LockResetRoundedIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                            {canRetry && (
                              <IconButton
                                size="small"
                                title="Executar retry"
                                sx={{
                                  color:
                                    item.status !== "failed" ? alpha("#1f2937", 0.35) : "#1f2937",
                                  border: `1px solid ${alpha("#1f2937", 0.14)}`,
                                  borderRadius: 2,
                                  backgroundColor: alpha("#1f2937", 0.03),
                                }}
                                disabled={item.status !== "failed" || retryingId === item.order_id}
                                onClick={() => handleRetry(item.order_id)}
                              >
                                <AutorenewRoundedIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Dialog
          open={isDetailsOpen}
          onClose={closeDetails}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              borderRadius: 3,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1.5 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Pedido {selectedOrderId || "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {selectedProcess?.customer_name || "Cliente não identificado"}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {selectedOrderId && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      resendingAccessId === selectedOrderId ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <LockResetRoundedIcon />
                      )
                    }
                    sx={secondaryButtonSx}
                    disabled={
                      selectedProcess?.status !== "success" || resendingAccessId === selectedOrderId
                    }
                    onClick={() => handleResendAccess(selectedOrderId)}
                  >
                    Reenviar acesso
                  </Button>
                )}
                {selectedOrderId && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LaunchRoundedIcon />}
                    sx={secondaryButtonSx}
                    onClick={() => openOrderPage(selectedOrderId)}
                  >
                    Abrir pedido
                  </Button>
                )}
                <IconButton onClick={closeDetails}>
                  <CloseRoundedIcon />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>

          <DialogContent dividers>
            {loadingDetails ? (
              <Stack direction="row" justifyContent="center" py={6}>
                <CircularProgress color="info" />
              </Stack>
            ) : detailsError ? (
              <Alert severity="error">{detailsError}</Alert>
            ) : !selectedProcess ? (
              <Alert severity="warning">Não foi possível carregar os detalhes do pedido.</Alert>
            ) : (
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ height: "100%", borderRadius: 2.5 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Status
                        </Typography>
                        <Box mt={1}>
                          <Chip
                            icon={getStatusMeta(selectedProcess.status).icon}
                            label={getStatusMeta(selectedProcess.status).label}
                            color={getStatusMeta(selectedProcess.status).color}
                            variant={
                              getStatusMeta(selectedProcess.status).color === "default"
                                ? "outlined"
                                : "filled"
                            }
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ height: "100%", borderRadius: 2.5 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Etapa atual
                        </Typography>
                        <Typography variant="h6" mt={1}>
                          {formatStepLabel(selectedProcess.current_step)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ height: "100%", borderRadius: 2.5 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Tentativas
                        </Typography>
                        <Typography variant="h6" mt={1}>
                          {selectedProcess.attempt_count || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ height: "100%", borderRadius: 2.5 }}>
                      <CardContent>
                        <Typography variant="overline" color="text.secondary">
                          Última atualização
                        </Typography>
                        <Typography variant="h6" mt={1}>
                          {formatDate(selectedProcess.updated_at || selectedProcess.finished_at)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ borderRadius: 2.5 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Resumo do pedido
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          CPF
                        </Typography>
                        <Typography variant="body2">{selectedProcess.cpf || "-"}</Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">
                          {selectedProcess.customer_email || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">
                          Última falha
                        </Typography>
                        <Typography variant="body2">
                          {formatStepLabel(selectedProcess.last_error_step)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Mensagem da última falha
                        </Typography>
                        <Typography variant="body2">
                          {selectedProcess.last_error_message || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: 2.5 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Resumo das etapas
                    </Typography>

                    {stepSummaryEntries.length === 0 ? (
                      <Alert severity="info">Sem resumo de etapas para este pedido.</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {stepSummaryEntries.map(([step, info]) => {
                          const stepStatus = getStatusMeta(info.status);

                          return (
                            <Grid item xs={12} md={6} key={step}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: `1px solid ${alpha("#1f2937", 0.08)}`,
                                  height: "100%",
                                }}
                              >
                                <Stack spacing={1.25}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="flex-start"
                                    spacing={1}
                                  >
                                    <Typography variant="button" color="#111827">
                                      {formatStepLabel(step)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      icon={stepStatus.icon}
                                      label={stepStatus.label}
                                      color={stepStatus.color}
                                      variant={
                                        stepStatus.color === "default" ? "outlined" : "filled"
                                      }
                                    />
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {info.message || "-"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Tentativa {info.attempt || 0} - Atualizado em{" "}
                                    {formatDate(info.updated_at)}
                                  </Typography>
                                </Stack>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: 2.5 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      Últimos eventos
                    </Typography>

                    {recentEvents.length === 0 ? (
                      <Alert severity="info">Sem eventos registrados para este pedido.</Alert>
                    ) : (
                      <Stack spacing={1.5}>
                        {recentEvents.map((event) => {
                          const eventStatus = getStatusMeta(event.status);

                          return (
                            <Box
                              key={event.id}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha("#1f2937", 0.08)}`,
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  justifyContent="space-between"
                                  spacing={1}
                                >
                                  <Typography variant="button" color="#111827">
                                    {formatStepLabel(event.step)}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    icon={eventStatus.icon}
                                    label={eventStatus.label}
                                    color={eventStatus.color}
                                    variant={
                                      eventStatus.color === "default" ? "outlined" : "filled"
                                    }
                                  />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {event.message || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tentativa {event.attempt || 0} - {formatDate(event.created_at)}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                <Card sx={{ borderRadius: 2.5 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={700} mb={2}>
                      JSON do step summary
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        borderRadius: 2,
                        overflow: "auto",
                        backgroundColor: "#0f172a",
                        color: "#e5eefb",
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      {formatJson(selectedProcess.step_summary)}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            )}
          </DialogContent>
        </Dialog>

        <MDSnackbar
          open={snackbar.open}
          color={snackbar.color}
          title={snackbar.title}
          content={snackbar.content}
          close={() => setSnackbar((current) => ({ ...current, open: false }))}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        />
      </Stack>
      <Footer />
    </DashboardLayout>
  );
}

export default List;
